/**
 * Security Screen - MOBILE ONLY
 * 
 * Change password functionality.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useState, useCallback } from 'react';
import { 
  View, Text, ScrollView, StyleSheet, TextInput, 
  Pressable, ActivityIndicator, KeyboardAvoidingView, Platform 
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useChangePassword } from '../../../hooks/useChangePassword';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';

export default function SecurityScreen() {
  const router = useRouter();
  const { isLoading, error, success, changePassword } = useChangePassword();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleBack = useCallback(() => router.back(), [router]);

  const isValidForm = currentPassword.length >= 1 && 
                      newPassword.length >= 8 && 
                      confirmPassword === newPassword;

  const handleSubmit = useCallback(async () => {
    if (!isValidForm || isLoading) return;
    const ok = await changePassword(currentPassword, newPassword);
    if (ok) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => router.back(), 1500);
    }
  }, [isValidForm, isLoading, currentPassword, newPassword, changePassword, router]);

  return (
    <ScreenContainer>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView style={styles.flex} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          {/* HEADER */}
          <View style={styles.header}>
            <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
              <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
            </Pressable>
            <Text style={styles.title}>Change Password</Text>
            <View style={styles.spacer} />
          </View>

          {/* STATUS MESSAGES */}
          {success && (
            <View style={styles.successBox}>
              <Ionicons name="checkmark-circle" size={icon.md} color={color.success} />
              <Text style={styles.successText}>Password changed successfully</Text>
            </View>
          )}
          {error && <View style={styles.errorBox}><Text style={styles.errorText}>{error}</Text></View>}

          {/* CURRENT PASSWORD */}
          <View style={styles.section}>
            <SectionHeader label="Current Password" />
            <ListCard>
              <PasswordInput
                value={currentPassword}
                onChangeText={setCurrentPassword}
                placeholder="Enter current password"
                showPassword={showCurrentPassword}
                onToggleShow={() => setShowCurrentPassword(!showCurrentPassword)}
              />
            </ListCard>
          </View>

          {/* NEW PASSWORD */}
          <View style={styles.section}>
            <SectionHeader label="New Password" />
            <ListCard>
              <PasswordInput
                value={newPassword}
                onChangeText={setNewPassword}
                placeholder="Enter new password (min 8 characters)"
                showPassword={showNewPassword}
                onToggleShow={() => setShowNewPassword(!showNewPassword)}
              />
              <PasswordInput
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder="Confirm new password"
                showPassword={showNewPassword}
                hideBorder
              />
            </ListCard>
            {newPassword.length > 0 && newPassword.length < 8 && (
              <Text style={styles.hint}>Password must be at least 8 characters</Text>
            )}
            {confirmPassword.length > 0 && confirmPassword !== newPassword && (
              <Text style={styles.hintError}>Passwords do not match</Text>
            )}
          </View>

          {/* SUBMIT BUTTON */}
          <View style={styles.section}>
            <Pressable
              onPress={handleSubmit}
              disabled={!isValidForm || isLoading}
              style={({ pressed }) => [styles.button, (!isValidForm || isLoading) && styles.buttonDisabled, pressed && styles.buttonPressed]}
            >
              {isLoading ? <ActivityIndicator size="small" color={color.textInverse} /> : <Text style={styles.buttonText}>Change Password</Text>}
            </Pressable>
          </View>

          <View style={styles.infoBox}>
            <Text style={styles.infoText}>If you've forgotten your current password, please contact an administrator.</Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}

// =============================================================================
// PASSWORD INPUT COMPONENT
// =============================================================================

interface PasswordInputProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  showPassword: boolean;
  onToggleShow?: () => void;
  hideBorder?: boolean;
}

function PasswordInput({ value, onChangeText, placeholder, showPassword, onToggleShow, hideBorder }: PasswordInputProps) {
  return (
    <View style={[styles.inputRow, hideBorder && styles.inputRowLast]}>
      <TextInput
        style={styles.input}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={color.textMuted}
        secureTextEntry={!showPassword}
        autoCapitalize="none"
        autoCorrect={false}
      />
      {onToggleShow && (
        <Pressable onPress={onToggleShow} style={styles.eyeButton}>
          <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={icon.md} color={color.textMuted} />
        </Pressable>
      )}
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  flex: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  successBox: { flexDirection: 'row', alignItems: 'center', marginHorizontal: space.md, marginBottom: space.md, padding: space.md, backgroundColor: color.successLight, borderRadius: radius.md, gap: space.sm },
  successText: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.success },
  errorBox: { marginHorizontal: space.md, marginBottom: space.md, padding: space.md, backgroundColor: color.errorLight, borderRadius: radius.md },
  errorText: { fontSize: text.caption.fontSize, color: color.error, textAlign: 'center' },
  inputRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: space.md, paddingHorizontal: space.md, borderBottomWidth: border.width.thin, borderBottomColor: color.divider, gap: space.sm },
  inputRowLast: { borderBottomWidth: 0 },
  input: { flex: 1, fontSize: text.body.fontSize, color: color.textPrimary, padding: 0 },
  eyeButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  hint: { fontSize: text.caption.fontSize, color: color.textMuted, marginTop: space.sm, paddingHorizontal: space.sm },
  hintError: { fontSize: text.caption.fontSize, color: color.error, marginTop: space.sm, paddingHorizontal: space.sm },
  button: { backgroundColor: color.primary, paddingVertical: space.md, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center', minHeight: touch.minimum },
  buttonDisabled: { opacity: 0.5 },
  buttonPressed: { opacity: 0.8 },
  buttonText: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textInverse },
  infoBox: { paddingHorizontal: space.lg, paddingVertical: space.md },
  infoText: { fontSize: text.caption.fontSize, color: color.textMuted, textAlign: 'center', lineHeight: text.body.lineHeight },
});
